import dayjs from 'dayjs'
import { Task, User, SelectChecklistState } from '../../types'
import { useState, useCallback, useRef } from 'react'
import { useUpdateTaskItems } from '../../api/aws/tasks/useUpdateTaskItems'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'

interface UseMtbMonthlyTasksArgs {
  orgId: string
  monthlyTasks: Task[]
  detectChanges?: () => void
}

export const useMtbMonthlyTasks = ({ orgId, monthlyTasks, detectChanges }: UseMtbMonthlyTasksArgs) => {
  const [monthlyTaskChanges, setMonthlyTaskChanges] = useState<Record<string, Partial<Task>>>({})
  const { mutate: updateTaskItem } = useUpdateTaskItems()
  const { showSnackbar } = useSnackbarContext()

  // Use ref to access latest monthlyTasks without stale closure issues
  const monthlyTasksRef = useRef(monthlyTasks)
  monthlyTasksRef.current = monthlyTasks

  const handleMonthlyTaskChange = useCallback((monthlyTask: Task, updates: Partial<Task>) => {
    // Access latest tasks via ref to avoid stale closure
    const currentTasks = monthlyTasksRef.current
    const original = currentTasks.find(st => st.SK === monthlyTask.SK)
    if (!original) return

    const changedFields: Partial<Task> = {}
    for (const [key, newValue] of Object.entries(updates)) {
      const originalValue = original[key as keyof Task]
      const isChanged =
        typeof newValue === 'object'
          ? JSON.stringify(originalValue) !== JSON.stringify(newValue)
          : originalValue !== newValue

      if (isChanged) {
        ;(changedFields as Record<string, unknown>)[key] = newValue
      }
    }

    // Nothing changed, don't update state
    if (Object.keys(changedFields).length === 0) return

    detectChanges?.()

    setMonthlyTaskChanges(prev => ({
      ...prev,
      [monthlyTask.SK]: {
        ...(prev[monthlyTask.SK] || {}),
        ...changedFields,
      },
    }))
  }, [detectChanges])

  const handleUpdateMonthlyTaskStatus = useCallback((monthlyTask: Task, newStatus: string, allTasksForOrg: Task[]) => {
    console.log('[handleUpdateMonthlyTaskStatus]', monthlyTask, newStatus)

    const updatePayload = {
      primaryKey: 'PK',
      sortKey: 'SK',
      tableName: 'TASKS_TABLE',
      pkValue: monthlyTask?.PK,
      skValue: monthlyTask?.SK,
      fieldName: 'masterStatus',
      newValue: newStatus,
    }

    updateTaskItem(
      { updates: [updatePayload] },
      {
        onSuccess: () => {
          // 🔄 Progress will be recalculated on the backend, so just refetch
          detectChanges()
          showSnackbar('Updated task status', 'success')
        },
      }
    )
  }, [updateTaskItem, detectChanges, showSnackbar])

  const updateMonthlyTaskActiveAssignee = useCallback((monthlyTask: Task, newActiveAssignee: User) => {
    console.log('[handleUpdateMonthlyTaskStatus]')

    const updatePayload = {
      primaryKey: 'PK',
      sortKey: 'SK',
      tableName: 'TASKS_TABLE',
      pkValue: monthlyTask?.PK,
      skValue: monthlyTask?.SK,
      fieldName: 'queue',
      newValue: newActiveAssignee,
    }

    updateTaskItem(
      { updates: [updatePayload] },
      {
        onSuccess: () => {
          // 🔄 Progress will be recalculated on the backend, so just refetch
          detectChanges()
          showSnackbar('Updated task status', 'success')
        },
      }
    )
  }, [updateTaskItem, detectChanges, showSnackbar])

  const handleUpdateMonthlyTaskProgress = useCallback((tasks: Task[]) => {
    const currentMonth = dayjs().format('YYYY-MM')

    const tasksForCurrentMonth = tasks.filter((task: Task) => {
      return dayjs(task?.startDate).format('YYYY-MM') === currentMonth
    })

    const progress: Record<string, string> = {}
    tasksForCurrentMonth.forEach((task: Task) => {
      if (task.SK && task.masterStatus) {
        progress[task.SK] = task.masterStatus
      }
    })

    // Note: This function currently doesn't have a task context to update
    // If you need to update a specific task's progress, pass it as a parameter
    console.log('[handleUpdateMonthlyTaskProgress] progress calculated:', progress)
  }, [])

  const updateMonthlyTaskProgressInternal = useCallback((
    targetTask: Task,
    updatedChecklist: SelectChecklistState,
    updatedChecklistDef?: Record<string, unknown>
  ) => {
    if (!targetTask?.PK || !targetTask?.SK) {
      console.warn('[updateMonthlyTaskProgress] Missing task PK or SK')
      return
    }

    const summary = {
      done: 0,
      inProgress: 0,
      stuck: 0,
      notStarted: 0,
      total: 0,
    }

    Object.values(updatedChecklist).forEach(value => {
      if (value === 'Done') summary.done++
      else if (value === 'In Progress') summary.inProgress++
      else if (value === 'Stuck') summary.stuck++
      else if (value === 'Not Started') summary.notStarted++
    })

    summary.total = Object.keys(updatedChecklist).length

    const updatePayload = [
      {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: targetTask.PK,
        skValue: targetTask.SK,
        fieldName: 'progress',
        newValue: updatedChecklist,
      },
      {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: targetTask.PK,
        skValue: targetTask.SK,
        fieldName: 'progressSummary',
        newValue: summary,
      },
    ]
    if (updatedChecklistDef) {
      updatePayload.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: targetTask.PK,
        skValue: targetTask.SK,
        fieldName: 'checklist',
        newValue: updatedChecklistDef as SelectChecklistState,
      })
    }
    updateTaskItem({ updates: updatePayload })
  }, [updateTaskItem])

  return {
    handleMonthlyTaskChange,
    handleUpdateMonthlyTaskStatus,
    handleUpdateMonthlyTaskProgress,
    updateMonthlyTaskProgressInternal,
    monthlyTaskChanges,
    setMonthlyTaskChanges,
    updateMonthlyTaskActiveAssignee,
  }
}
